import { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../../context/AuthContext';
import { OwnerLayout } from '../../components/layouts/OwnerLayout';
import { format } from 'date-fns';
import { es } from 'date-fns/locale';
import { FileText, User, Package, Download, Calendar, MapPin, Filter, BarChart3, Edit2, Clock, Trash2, History, Plus, Minus, Camera, X, Check, FileDown, Send, Mail, Link2, AlertTriangle, Loader2 } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from '../../components/ui/dialog';
import { Checkbox } from '../../components/ui/checkbox';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from '../../components/ui/alert-dialog';
import { toast } from 'sonner';
import { getErrorMessage } from '../../lib/errors';

const Reports = () => {
  const { api, user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [workers, setWorkers] = useState([]);
  const [clients, setClients] = useState([]);
  const [materials, setMaterials] = useState([]);
  const [reportData, setReportData] = useState(null);
  const [clientReportData, setClientReportData] = useState(null);
  const [selectedMonth, setSelectedMonth] = useState(format(new Date(), 'yyyy-MM'));
  const [selectedWorker, setSelectedWorker] = useState('');
  const [selectedClient, setSelectedClient] = useState('');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [loadingClientReport, setLoadingClientReport] = useState(false);
  
  // All reports tab state
  const [allReports, setAllReports] = useState([]);
  const [loadingAll, setLoadingAll] = useState(false);
  const [allFilterType, setAllFilterType] = useState('all');
  const [sendingEmail, setSendingEmail] = useState(null);
  const [sendingCombined, setSendingCombined] = useState(null);
  
  // Edit modal state
  const [editModalOpen, setEditModalOpen] = useState(false);
  const [editingReport, setEditingReport] = useState(null);
  const [editForm, setEditForm] = useState({
    started_at: '',
    completed_at: '',
    observations: '',
    checklist: [],
    parameters: [],
    materials_used: [],
    manual_duration: ''
  });
  const [saving, setSaving] = useState(false);
  
  // Delete confirmation
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [reportToDelete, setReportToDelete] = useState(null);
  const [deleting, setDeleting] = useState(false);
  
  // History modal
  const [historyModalOpen, setHistoryModalOpen] = useState(false);
  const [historyData, setHistoryData] = useState([]);
  const [loadingHistory, setLoadingHistory] = useState(false);

  const isOwner = user?.role === 'owner';

  const fetchWorkers = useCallback(async () => {
    try {
      const res = await api.get('/workers');
      setWorkers(res.data);
    } catch (error) {
      console.error('Error fetching workers:', error);
    }
  }, [api]);

  const fetchClients = useCallback(async () => {
    try {
      const res = await api.get('/clients');
      setClients(res.data);
    } catch (error) {
      console.error('Error fetching clients:', error);
    }
  }, [api]);

  const fetchMaterials = useCallback(async () => {
    try {
      const res = await api.get('/materials');
      setMaterials(res.data);
    } catch (error) {
      console.error('Error fetching materials:', error);
    }
  }, [api]);

  const fetchReport = useCallback(async () => {
    setLoading(true);
    try {
      const parts = selectedMonth.split('-');
      const year = parseInt(parts[0]);
      const month = parseInt(parts[1]);
      const params = { year, month };
      if (selectedWorker) {
        params.worker_id = selectedWorker;
      }
      const res = await api.get('/reports/monthly', { params });
      setReportData(res.data);
    } catch (error) {
      toast.error('Error al cargar el reporte');
    } finally {
      setLoading(false);
    }
  }, [api, selectedMonth, selectedWorker]);

  const fetchClientReport = useCallback(async () => {
    if (!selectedClient) {
      setClientReportData(null);
      return;
    }
    
    setLoadingClientReport(true);
    try {
      const params = {};
      if (dateFrom) params.date_from = dateFrom;
      if (dateTo) params.date_to = dateTo;
      
      const res = await api.get(`/reports/client/${selectedClient}`, { params });
      setClientReportData(res.data);
    } catch (error) {
      toast.error('Error al cargar el reporte del cliente');
    } finally {
      setLoadingClientReport(false);
    }
  }, [api, selectedClient, dateFrom, dateTo]);

  const fetchAllReports = useCallback(async () => {
    setLoadingAll(true);
    try {
      const res = await api.get('/work-reports');
      let data = res.data || [];
      if (allFilterType === 'incidencia') {
        data = data.filter(r => r.report_type === 'incidencia');
      } else if (allFilterType === 'normal') {
        data = data.filter(r => r.report_type !== 'incidencia');
      }
      // Sort by completed_at descending
      data.sort((a, b) => new Date(b.completed_at) - new Date(a.completed_at));
      setAllReports(data);
    } catch (error) {
      toast.error('Error al cargar partes');
    } finally {
      setLoadingAll(false);
    }
  }, [api, allFilterType]);

  const sendReportEmail = async (reportId) => {
    setSendingEmail(reportId);
    try {
      await api.post(`/work-reports/${reportId}/send-email`);
      toast.success('Parte enviado por email al cliente');
    } catch (error) {
      toast.error(getErrorMessage(error, 'Error al enviar el email'));
    } finally {
      setSendingEmail(null);
    }
  };

  const sendCombinedEmail = async (reportId) => {
    setSendingCombined(reportId);
    try {
      await api.post(`/work-reports/${reportId}/send-combined`);
      toast.success('Incidencia y reparación enviados juntos por email');
    } catch (error) {
      toast.error(getErrorMessage(error, 'Error al enviar los partes'));
    } finally {
      setSendingCombined(null);
    }
  };

  useEffect(() => {
    fetchWorkers();
    fetchClients();
    fetchMaterials();
    fetchAllReports();
  }, [fetchWorkers, fetchClients, fetchMaterials, fetchAllReports]);

  useEffect(() => {
    fetchReport();
  }, [fetchReport]);

  useEffect(() => {
    if (selectedClient) {
      fetchClientReport();
    }
  }, [selectedClient, fetchClientReport]);

  const getMonthsList = () => {
    const result = [];
    const currentYear = new Date().getFullYear();
    for (let year = currentYear; year >= currentYear - 1; year--) {
      for (let i = 11; i >= 0; i--) {
        const date = new Date(year, i, 1);
        if (date <= new Date()) {
          result.push({
            value: format(date, 'yyyy-MM'),
            label: format(date, 'MMMM yyyy', { locale: es })
          });
        }
      }
    }
    return result.slice(0, 24);
  };

  const downloadPdf = async (reportId) => {
    const loadingToast = toast.loading('Generando PDF...');
    try {
      const token = localStorage.getItem('token');
      const url = `${process.env.REACT_APP_BACKEND_URL}/api/work-reports/${reportId}/pdf`;
      const res = await fetch(url, { headers: { Authorization: `Bearer ${token}` } });
      
      if (!res.ok) {
        throw new Error('Error al generar PDF');
      }
      
      const blob = await res.blob();
      const link = document.createElement('a');
      link.href = URL.createObjectURL(blob);
      link.download = `parte_${reportId.slice(0, 8)}.pdf`;
      link.click();
      
      toast.dismiss(loadingToast);
      toast.success('PDF descargado correctamente');
    } catch (error) {
      toast.dismiss(loadingToast);
      toast.error('Error al descargar PDF');
    }
  };

  const downloadClientHistoryPdf = async () => {
    if (!selectedClient) return;
    
    const loadingToast = toast.loading('Generando historial PDF...');
    try {
      const token = localStorage.getItem('token');
      let url = `${process.env.REACT_APP_BACKEND_URL}/api/clients/${selectedClient}/history-pdf`;
      
      // Add date filters if specified
      const params = new URLSearchParams();
      if (dateFrom) params.append('date_from', dateFrom);
      if (dateTo) params.append('date_to', dateTo);
      if (params.toString()) url += `?${params.toString()}`;
      
      const res = await fetch(url, { headers: { Authorization: `Bearer ${token}` } });
      
      if (!res.ok) {
        const errorData = await res.json();
        throw new Error(errorData.detail || 'Error al generar PDF');
      }
      
      const blob = await res.blob();
      const link = document.createElement('a');
      link.href = URL.createObjectURL(blob);
      
      // Get client name for filename
      const clientName = clients.find(c => c.id === selectedClient)?.name || 'cliente';
      link.download = `historial_${clientName.replace(/\s+/g, '_').slice(0, 20)}.pdf`;
      link.click();
      
      toast.dismiss(loadingToast);
      toast.success('Historial PDF descargado correctamente');
    } catch (error) {
      toast.dismiss(loadingToast);
      toast.error(error.message || 'Error al descargar el historial');
    }
  };

  const openEditModal = (report) => {
    setEditingReport(report);
    setEditForm({
      started_at: report.started_at ? report.started_at.slice(0, 16) : '',
      completed_at: report.completed_at ? report.completed_at.slice(0, 16) : '',
      observations: report.observations || '',
      checklist: report.checklist || [],
      parameters: report.parameters || [],
      materials_used: report.materials_used || [],
      manual_duration: report.manual_duration || ''
    });
    setEditModalOpen(true);
  };

  const handleChecklistToggle = (index) => {
    const newChecklist = [...editForm.checklist];
    newChecklist[index] = { ...newChecklist[index], checked: !newChecklist[index].checked };
    setEditForm({ ...editForm, checklist: newChecklist });
  };

  const handleParameterChange = (index, field, value) => {
    const newParams = [...editForm.parameters];
    newParams[index] = { ...newParams[index], [field]: value };
    setEditForm({ ...editForm, parameters: newParams });
  };

  const handleMaterialQuantityChange = (index, delta) => {
    const newMaterials = [...editForm.materials_used];
    const currentQty = parseFloat(newMaterials[index].quantity) || 0;
    const newQty = Math.max(0, currentQty + delta);
    newMaterials[index] = { ...newMaterials[index], quantity: newQty };
    setEditForm({ ...editForm, materials_used: newMaterials });
  };

  const removeMaterial = (index) => {
    const newMaterials = editForm.materials_used.filter((_, i) => i !== index);
    setEditForm({ ...editForm, materials_used: newMaterials });
  };

  const handleSaveEdit = async () => {
    if (!editingReport) return;
    setSaving(true);
    try {
      const updateData = {
        started_at: editForm.started_at ? new Date(editForm.started_at).toISOString() : null,
        completed_at: editForm.completed_at ? new Date(editForm.completed_at).toISOString() : null,
        observations: editForm.observations,
        checklist: editForm.checklist,
        parameters: editForm.parameters,
        materials_used: editForm.materials_used,
        manual_duration: editForm.manual_duration ? parseInt(editForm.manual_duration) : null
      };
      
      await api.put(`/work-reports/${editingReport.id}`, updateData);
      toast.success('Parte actualizado correctamente');
      setEditModalOpen(false);
      fetchReport();
      if (selectedClient) {
        fetchClientReport();
      }
    } catch (error) {
      toast.error('Error al actualizar el parte');
    } finally {
      setSaving(false);
    }
  };

  const openDeleteDialog = (report) => {
    setReportToDelete(report);
    setDeleteDialogOpen(true);
  };

  const handleDelete = async () => {
    if (!reportToDelete) return;
    setDeleting(true);
    try {
      await api.delete(`/work-reports/${reportToDelete.id}`);
      toast.success('Parte eliminado correctamente');
      setDeleteDialogOpen(false);
      setReportToDelete(null);
      fetchReport();
      if (selectedClient) {
        fetchClientReport();
      }
    } catch (error) {
      toast.error('Error al eliminar el parte');
    } finally {
      setDeleting(false);
    }
  };

  const openHistoryModal = async (report) => {
    setLoadingHistory(true);
    setHistoryModalOpen(true);
    try {
      const res = await api.get(`/work-reports/${report.id}/history`);
      setHistoryData(res.data.history || []);
    } catch (error) {
      toast.error('Error al cargar el historial');
      setHistoryData([]);
    } finally {
      setLoadingHistory(false);
    }
  };

  const formatFieldName = (field) => {
    const names = {
      started_at: 'Hora de inicio',
      completed_at: 'Hora de fin',
      observations: 'Observaciones',
      checklist: 'Checklist',
      parameters: 'Parámetros',
      materials_used: 'Materiales',
      photos: 'Fotos',
      manual_duration: 'Duración manual'
    };
    return names[field] || field;
  };

  const monthsList = getMonthsList();
  const workerSummary = reportData?.worker_summary || [];
  const materialsSummary = reportData?.materials_summary || [];
  const reports = reportData?.reports || [];

  const ReportCard = ({ report, showClientName = true }) => {
    const isIncidencia = report.report_type === 'incidencia';
    
    return (
      <div className={`flex items-center justify-between p-4 rounded-lg transition-colors ${
        isIncidencia 
          ? 'bg-red-50 hover:bg-red-100 border border-red-200' 
          : 'bg-slate-50 hover:bg-slate-100'
      }`}>
        <div className="flex-1 min-w-0 pr-2">
          <div className="flex items-center gap-2 mb-1 flex-wrap">
            {showClientName && (
              <h4 className={`font-medium truncate ${isIncidencia ? 'text-red-800' : ''}`}>
                {report.client_name || 'Cliente'}
              </h4>
            )}
            {isIncidencia && (
              <Badge className="text-xs bg-red-600 text-white whitespace-nowrap">
                Incidencia
              </Badge>
            )}
            <Badge variant="outline" className="text-xs whitespace-nowrap">
              {(report.checklist || []).filter(c => c.checked).length}/{(report.checklist || []).length} tareas
            </Badge>
            {report.is_adhoc && (
              <Badge className="text-xs bg-amber-100 text-amber-800 whitespace-nowrap">Ad-hoc</Badge>
            )}
            {(report.linked_incident_id || report.linked_repair_id) && (
              <Badge className="text-xs bg-purple-100 text-purple-800 whitespace-nowrap">
                <Link2 className="w-3 h-3 mr-1" />
                Vinculado
              </Badge>
            )}
          {(report.edit_history?.length > 0) && (
            <Badge variant="secondary" className="text-xs whitespace-nowrap">
              <History className="w-3 h-3 mr-1" />
              {report.edit_history.length} ediciones
            </Badge>
          )}
        </div>
        <div className="flex items-center gap-2 sm:gap-4 text-sm text-slate-500 flex-wrap">
          <span className="flex items-center gap-1 whitespace-nowrap">
            <Calendar className="w-3 h-3" />
            {report.completed_at ? format(new Date(report.completed_at), "d MMM yyyy", { locale: es }) : '-'}
          </span>
          <span className="flex items-center gap-1 whitespace-nowrap hidden sm:flex">
            <Clock className="w-3 h-3" />
            {report.started_at ? report.started_at.slice(11, 16) : '--:--'} - {report.completed_at ? report.completed_at.slice(11, 16) : '--:--'}
          </span>
          <span className="flex items-center gap-1 whitespace-nowrap">
            <User className="w-3 h-3" />
            <span className="truncate max-w-[80px] sm:max-w-none">{report.worker_name || 'Técnico'}</span>
          </span>
          {report.photos?.length > 0 && (
            <Badge variant="outline" className="text-xs hidden sm:flex">{report.photos.length} fotos</Badge>
          )}
        </div>
      </div>
      <div className="flex gap-1 sm:gap-2 flex-shrink-0">
        {/* Send email button */}
        <Button
          variant="ghost"
          size="sm"
          onClick={() => sendReportEmail(report.id)}
          disabled={sendingEmail === report.id}
          data-testid={`send-email-${report.id}`}
          className="h-8 w-8 p-0 sm:h-9 sm:w-auto sm:px-3 text-sky-600 hover:text-sky-700 hover:bg-sky-50"
          title="Enviar por email"
        >
          {sendingEmail === report.id ? (
            <Loader2 className="w-4 h-4 animate-spin" />
          ) : (
            <>
              <Mail className="w-4 h-4" />
              <span className="hidden sm:inline ml-1">Enviar</span>
            </>
          )}
        </Button>
        {/* Send combined (incident + repair) */}
        {(report.linked_incident_id || report.linked_repair_id) && (
          <Button
            variant="ghost"
            size="sm"
            onClick={() => sendCombinedEmail(report.id)}
            disabled={sendingCombined === report.id}
            data-testid={`send-combined-${report.id}`}
            className="h-8 w-8 p-0 sm:h-9 sm:w-auto sm:px-3 text-purple-600 hover:text-purple-700 hover:bg-purple-50"
            title="Enviar incidencia + reparación juntos"
          >
            {sendingCombined === report.id ? (
              <Loader2 className="w-4 h-4 animate-spin" />
            ) : (
              <>
                <Link2 className="w-4 h-4" />
                <span className="hidden sm:inline ml-1">Conjunto</span>
              </>
            )}
          </Button>
        )}
        {(report.edit_history?.length > 0) && (
          <Button
            variant="ghost"
            size="sm"
            onClick={() => openHistoryModal(report)}
            className="h-8 w-8 p-0"
            title="Ver historial"
          >
            <History className="w-4 h-4 text-slate-500" />
          </Button>
        )}
        <Button
          variant="ghost"
          size="sm"
          onClick={() => openEditModal(report)}
          data-testid={`edit-report-${report.id}`}
          className="h-8 w-8 p-0 sm:h-9 sm:w-auto sm:px-3"
        >
          <Edit2 className="w-4 h-4" />
          <span className="hidden sm:inline ml-1">Editar</span>
        </Button>
        <Button
          variant="outline"
          size="sm"
          onClick={() => downloadPdf(report.id)}
          data-testid={`download-pdf-${report.id}`}
          className="h-8 w-8 p-0 sm:h-9 sm:w-auto sm:px-3"
        >
          <Download className="w-4 h-4" />
          <span className="hidden sm:inline ml-1">PDF</span>
        </Button>
        {isOwner && (
          <Button
            variant="ghost"
            size="sm"
            onClick={() => openDeleteDialog(report)}
            data-testid={`delete-report-${report.id}`}
            className="h-8 w-8 p-0 text-red-600 hover:text-red-700 hover:bg-red-50"
          >
            <Trash2 className="w-4 h-4" />
          </Button>
        )}
      </div>
    </div>
  );
  };

  return (
    <OwnerLayout>
      <div className="space-y-6" data-testid="reports-page">
        <div>
          <h1 className="font-display text-2xl font-bold text-slate-900">Reportes</h1>
          <p className="text-slate-500">Análisis y resúmenes de actividad</p>
        </div>

        <Tabs defaultValue="all" className="space-y-6" onValueChange={(v) => { if (v === 'all') fetchAllReports(); }}>
          <TabsList>
            <TabsTrigger value="all" data-testid="tab-all-reports">
              <FileText className="w-4 h-4 mr-2" />
              Todos los Partes
            </TabsTrigger>
            <TabsTrigger value="monthly">
              <BarChart3 className="w-4 h-4 mr-2" />
              Resumen Mensual
            </TabsTrigger>
            <TabsTrigger value="client">
              <User className="w-4 h-4 mr-2" />
              Por Cliente
            </TabsTrigger>
          </TabsList>

          {/* All Reports Tab */}
          <TabsContent value="all" className="space-y-6">
            <div className="flex flex-col sm:flex-row gap-3 items-start sm:items-center justify-between">
              <div className="flex gap-2">
                <Button
                  variant={allFilterType === 'all' ? 'default' : 'outline'}
                  size="sm"
                  onClick={() => setAllFilterType('all')}
                  data-testid="filter-all"
                >
                  Todos
                </Button>
                <Button
                  variant={allFilterType === 'normal' ? 'default' : 'outline'}
                  size="sm"
                  onClick={() => setAllFilterType('normal')}
                  data-testid="filter-normal"
                >
                  Trabajo
                </Button>
                <Button
                  variant={allFilterType === 'incidencia' ? 'default' : 'outline'}
                  size="sm"
                  onClick={() => setAllFilterType('incidencia')}
                  className={allFilterType === 'incidencia' ? 'bg-red-600 hover:bg-red-700' : ''}
                  data-testid="filter-incidencia"
                >
                  <AlertTriangle className="w-4 h-4 mr-1" />
                  Incidencias
                </Button>
              </div>
              <Button variant="outline" size="sm" onClick={fetchAllReports} data-testid="refresh-all-reports">
                Actualizar
              </Button>
            </div>

            {loadingAll ? (
              <div className="flex items-center justify-center h-64">
                <Loader2 className="w-8 h-8 animate-spin text-sky-600" />
              </div>
            ) : allReports.length > 0 ? (
              <Card>
                <CardHeader>
                  <CardTitle className="text-lg">Partes ({allReports.length})</CardTitle>
                  <CardDescription>Haz clic en "Enviar" para enviar el PDF al email del cliente</CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="space-y-3">
                    {allReports.map((report) => (
                      <ReportCard key={report.id} report={report} />
                    ))}
                  </div>
                </CardContent>
              </Card>
            ) : (
              <Card>
                <CardContent className="text-center py-12">
                  <FileText className="w-12 h-12 mx-auto mb-4 text-slate-300" />
                  <p className="text-slate-500">No hay partes de trabajo</p>
                </CardContent>
              </Card>
            )}
          </TabsContent>

          {/* Monthly Report Tab */}
          <TabsContent value="monthly" className="space-y-6">
            <div className="flex flex-col sm:flex-row gap-3">
              <Select value={selectedMonth} onValueChange={setSelectedMonth}>
                <SelectTrigger className="w-[200px]">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {monthsList.map((m) => (
                    <SelectItem key={m.value} value={m.value}>{m.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <Select value={selectedWorker || "all"} onValueChange={(v) => setSelectedWorker(v === "all" ? "" : v)}>
                <SelectTrigger className="w-[200px]">
                  <SelectValue placeholder="Todos los trabajadores" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Todos los trabajadores</SelectItem>
                  {workers.map((w) => (
                    <SelectItem key={w.id} value={w.id}>{w.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {loading ? (
              <div className="flex items-center justify-center h-64">
                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-sky-600"></div>
              </div>
            ) : (
              <>
                {/* Stats Cards */}
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <Card>
                    <CardContent className="p-6">
                      <div className="flex items-center gap-4">
                        <div className="w-12 h-12 rounded-lg bg-sky-100 flex items-center justify-center">
                          <FileText className="w-6 h-6 text-sky-600" />
                        </div>
                        <div>
                          <p className="text-3xl font-bold">{reportData?.total_reports || 0}</p>
                          <p className="text-sm text-slate-500">Partes completados</p>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                  <Card>
                    <CardContent className="p-6">
                      <div className="flex items-center gap-4">
                        <div className="w-12 h-12 rounded-lg bg-purple-100 flex items-center justify-center">
                          <User className="w-6 h-6 text-purple-600" />
                        </div>
                        <div>
                          <p className="text-3xl font-bold">{workerSummary.length}</p>
                          <p className="text-sm text-slate-500">Trabajadores activos</p>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                  <Card>
                    <CardContent className="p-6">
                      <div className="flex items-center gap-4">
                        <div className="w-12 h-12 rounded-lg bg-green-100 flex items-center justify-center">
                          <Package className="w-6 h-6 text-green-600" />
                        </div>
                        <div>
                          <p className="text-3xl font-bold">{materialsSummary.length}</p>
                          <p className="text-sm text-slate-500">Materiales usados</p>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                </div>

                {/* Worker Summary */}
                {workerSummary.length > 0 && (
                  <Card>
                    <CardHeader>
                      <CardTitle className="text-lg">Rendimiento por trabajador</CardTitle>
                    </CardHeader>
                    <CardContent>
                      <div className="space-y-3">
                        {workerSummary.map((ws) => (
                          <div key={ws.worker_id} className="flex items-center justify-between p-4 bg-slate-50 rounded-lg">
                            <div className="flex items-center gap-3">
                              <div className="w-10 h-10 rounded-full bg-sky-100 flex items-center justify-center">
                                <User className="w-5 h-5 text-sky-600" />
                              </div>
                              <span className="font-medium">{ws.worker_name}</span>
                            </div>
                            <div className="text-right">
                              <p className="font-bold">{ws.total_jobs} trabajos</p>
                              <p className="text-sm text-slate-500">{ws.total_reports} partes</p>
                            </div>
                          </div>
                        ))}
                      </div>
                    </CardContent>
                  </Card>
                )}

                {/* Materials Summary */}
                {materialsSummary.length > 0 && (
                  <Card>
                    <CardHeader>
                      <CardTitle className="text-lg">Materiales utilizados</CardTitle>
                    </CardHeader>
                    <CardContent>
                      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
                        {materialsSummary.map((mat, idx) => (
                          <div key={idx} className="p-4 bg-slate-50 rounded-lg">
                            <h4 className="font-medium">{mat.name}</h4>
                            <p className="text-2xl font-bold">
                              {mat.total_quantity} <span className="text-sm font-normal text-slate-500">{mat.unit}</span>
                            </p>
                          </div>
                        ))}
                      </div>
                    </CardContent>
                  </Card>
                )}

                {/* Reports List */}
                {reports.length > 0 ? (
                  <Card>
                    <CardHeader>
                      <CardTitle className="text-lg">Partes de trabajo ({reports.length})</CardTitle>
                    </CardHeader>
                    <CardContent>
                      <div className="space-y-3">
                        {reports.map((report) => (
                          <ReportCard key={report.id} report={report} />
                        ))}
                      </div>
                    </CardContent>
                  </Card>
                ) : (
                  <Card>
                    <CardContent className="text-center py-12">
                      <FileText className="w-12 h-12 mx-auto mb-4 text-slate-300" />
                      <p className="text-slate-500">No hay reportes para el período seleccionado</p>
                    </CardContent>
                  </Card>
                )}
              </>
            )}
          </TabsContent>

          {/* Client Report Tab */}
          <TabsContent value="client" className="space-y-6">
            <Card>
              <CardHeader>
                <CardTitle className="text-lg flex items-center gap-2">
                  <Filter className="w-5 h-5" />
                  Filtros
                </CardTitle>
                <CardDescription>Selecciona un cliente y el rango de fechas para ver su historial</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                  <div className="space-y-2">
                    <Label>Cliente</Label>
                    <Select value={selectedClient || "none"} onValueChange={(v) => setSelectedClient(v === "none" ? "" : v)}>
                      <SelectTrigger>
                        <SelectValue placeholder="Seleccionar cliente" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="none">Seleccionar cliente</SelectItem>
                        {clients.map((c) => (
                          <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-2">
                    <Label>Desde</Label>
                    <Input 
                      type="date" 
                      value={dateFrom} 
                      onChange={(e) => setDateFrom(e.target.value)} 
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Hasta</Label>
                    <Input 
                      type="date" 
                      value={dateTo} 
                      onChange={(e) => setDateTo(e.target.value)} 
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>&nbsp;</Label>
                    <Button 
                      onClick={fetchClientReport} 
                      disabled={!selectedClient || loadingClientReport}
                      className="w-full"
                    >
                      {loadingClientReport ? 'Cargando...' : 'Generar reporte'}
                    </Button>
                  </div>
                </div>
              </CardContent>
            </Card>

            {loadingClientReport ? (
              <div className="flex items-center justify-center h-64">
                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-sky-600"></div>
              </div>
            ) : clientReportData ? (
              <>
                {/* Client Info */}
                <Card>
                  <CardHeader className="flex flex-row items-start justify-between">
                    <div>
                      <CardTitle className="text-lg">{clientReportData.client?.name}</CardTitle>
                      <CardDescription className="flex items-center gap-2">
                        <MapPin className="w-4 h-4" />
                        {clientReportData.client?.address || 'Sin dirección'}
                      </CardDescription>
                    </div>
                    <Button
                      onClick={downloadClientHistoryPdf}
                      className="bg-gradient-to-r from-sky-600 to-cyan-600 hover:from-sky-700 hover:to-cyan-700"
                      data-testid="download-client-history-pdf"
                    >
                      <FileDown className="w-4 h-4 mr-2" />
                      Exportar Historial PDF
                    </Button>
                  </CardHeader>
                  <CardContent>
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                      <div className="text-center p-4 bg-slate-50 rounded-lg">
                        <p className="text-2xl font-bold text-sky-600">{clientReportData.total_jobs}</p>
                        <p className="text-sm text-slate-500">Trabajos totales</p>
                      </div>
                      <div className="text-center p-4 bg-slate-50 rounded-lg">
                        <p className="text-2xl font-bold text-green-600">{clientReportData.total_reports}</p>
                        <p className="text-sm text-slate-500">Partes completados</p>
                      </div>
                      <div className="text-center p-4 bg-slate-50 rounded-lg">
                        <p className="text-2xl font-bold text-purple-600">
                          {(clientReportData.reports || []).reduce((acc, r) => acc + (r.photos?.length || 0), 0)}
                        </p>
                        <p className="text-sm text-slate-500">Fotos totales</p>
                      </div>
                      <div className="text-center p-4 bg-slate-50 rounded-lg">
                        <p className="text-2xl font-bold text-amber-600">
                          {(clientReportData.reports || []).reduce((acc, r) => acc + (r.materials_used?.length || 0), 0)}
                        </p>
                        <p className="text-sm text-slate-500">Materiales usados</p>
                      </div>
                    </div>
                  </CardContent>
                </Card>

                {/* Client Reports */}
                {(clientReportData.reports || []).length > 0 ? (
                  <Card>
                    <CardHeader>
                      <CardTitle className="text-lg">Historial de partes ({(clientReportData.reports || []).length})</CardTitle>
                    </CardHeader>
                    <CardContent>
                      <div className="space-y-3">
                        {(clientReportData.reports || []).map((report) => (
                          <ReportCard key={report.id} report={report} showClientName={false} />
                        ))}
                      </div>
                    </CardContent>
                  </Card>
                ) : (
                  <Card>
                    <CardContent className="text-center py-12">
                      <FileText className="w-12 h-12 mx-auto mb-4 text-slate-300" />
                      <p className="text-slate-500">No hay partes de trabajo para este cliente en el período seleccionado</p>
                    </CardContent>
                  </Card>
                )}
              </>
            ) : (
              <Card>
                <CardContent className="text-center py-12">
                  <User className="w-12 h-12 mx-auto mb-4 text-slate-300" />
                  <p className="text-slate-500">Selecciona un cliente para ver su historial de partes</p>
                </CardContent>
              </Card>
            )}
          </TabsContent>
        </Tabs>

        {/* Edit Report Modal - Full Edit */}
        <Dialog open={editModalOpen} onOpenChange={setEditModalOpen}>
          <DialogContent className="sm:max-w-2xl max-h-[90vh] flex flex-col p-0">
            <DialogHeader className="px-6 pt-6 pb-4 flex-shrink-0">
              <DialogTitle>Editar Parte de Trabajo</DialogTitle>
              <DialogDescription>
                Modifica los datos del parte. Los cambios quedarán registrados en el historial.
              </DialogDescription>
            </DialogHeader>
            <div className="flex-1 overflow-y-auto px-6" style={{ maxHeight: 'calc(90vh - 180px)' }}>
              <div className="space-y-6 py-4">
                {/* Times */}
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>Hora de inicio</Label>
                    <Input
                      type="datetime-local"
                      value={editForm.started_at}
                      onChange={(e) => setEditForm({ ...editForm, started_at: e.target.value })}
                      data-testid="edit-started-at"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Hora de finalización</Label>
                    <Input
                      type="datetime-local"
                      value={editForm.completed_at}
                      onChange={(e) => setEditForm({ ...editForm, completed_at: e.target.value })}
                      data-testid="edit-completed-at"
                    />
                  </div>
                </div>

                {/* Manual Duration */}
                <div className="space-y-2">
                  <Label>Duración manual (minutos)</Label>
                  <Input
                    type="number"
                    value={editForm.manual_duration}
                    onChange={(e) => setEditForm({ ...editForm, manual_duration: e.target.value })}
                    placeholder="Dejar vacío para calcular automáticamente"
                  />
                </div>

                {/* Checklist */}
                {editForm.checklist.length > 0 && (
                  <div className="space-y-2">
                    <Label>Checklist</Label>
                    <div className="space-y-2 p-3 bg-slate-50 rounded-lg">
                      {editForm.checklist.map((item, idx) => (
                        <div key={item.id || idx} className="flex items-center gap-3">
                          <Checkbox
                            checked={item.checked}
                            onCheckedChange={() => handleChecklistToggle(idx)}
                          />
                          <span className={item.checked ? 'line-through text-slate-500' : ''}>
                            {item.label}
                          </span>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Parameters */}
                {editForm.parameters.length > 0 && (
                  <div className="space-y-2">
                    <Label>Parámetros</Label>
                    <div className="space-y-2">
                      {editForm.parameters.map((param, idx) => (
                        <div key={param.id || idx} className="flex items-center gap-2 p-3 bg-slate-50 rounded-lg">
                          <span className="font-medium min-w-[100px]">{param.name}</span>
                          <Input
                            type="text"
                            value={param.value || ''}
                            onChange={(e) => handleParameterChange(idx, 'value', e.target.value)}
                            className="flex-1"
                          />
                          {param.unit && <span className="text-slate-500">{param.unit}</span>}
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Materials */}
                {editForm.materials_used.length > 0 && (
                  <div className="space-y-2">
                    <Label>Materiales utilizados</Label>
                    <div className="space-y-2">
                      {editForm.materials_used.map((mat, idx) => (
                        <div key={idx} className="flex items-center gap-2 p-3 bg-slate-50 rounded-lg">
                          <span className="font-medium flex-1">{mat.name}</span>
                          <div className="flex items-center gap-1">
                            <Button
                              variant="outline"
                              size="sm"
                              className="h-8 w-8 p-0"
                              onClick={() => handleMaterialQuantityChange(idx, -0.5)}
                            >
                              <Minus className="w-4 h-4" />
                            </Button>
                            <span className="w-16 text-center font-medium">
                              {mat.quantity} {mat.unit}
                            </span>
                            <Button
                              variant="outline"
                              size="sm"
                              className="h-8 w-8 p-0"
                              onClick={() => handleMaterialQuantityChange(idx, 0.5)}
                            >
                              <Plus className="w-4 h-4" />
                            </Button>
                            <Button
                              variant="ghost"
                              size="sm"
                              className="h-8 w-8 p-0 text-red-600"
                              onClick={() => removeMaterial(idx)}
                            >
                              <X className="w-4 h-4" />
                            </Button>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Observations */}
                <div className="space-y-2">
                  <Label>Observaciones</Label>
                  <Textarea
                    value={editForm.observations}
                    onChange={(e) => setEditForm({ ...editForm, observations: e.target.value })}
                    rows={3}
                    data-testid="edit-observations"
                  />
                </div>
              </div>
            </div>
            <DialogFooter className="px-6 py-4 border-t flex-shrink-0">
              <Button variant="outline" onClick={() => setEditModalOpen(false)}>
                Cancelar
              </Button>
              <Button onClick={handleSaveEdit} disabled={saving}>
                {saving ? 'Guardando...' : 'Guardar cambios'}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Delete Confirmation Dialog */}
        <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle>¿Eliminar este parte?</AlertDialogTitle>
              <AlertDialogDescription>
                Esta acción no se puede deshacer. El parte de trabajo será eliminado permanentemente.
                {reportToDelete?.job_id && (
                  <span className="block mt-2 text-amber-600">
                    El trabajo asociado volverá al estado "Programado".
                  </span>
                )}
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel>Cancelar</AlertDialogCancel>
              <AlertDialogAction
                onClick={handleDelete}
                disabled={deleting}
                className="bg-red-600 hover:bg-red-700"
              >
                {deleting ? 'Eliminando...' : 'Eliminar'}
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>

        {/* History Modal */}
        <Dialog open={historyModalOpen} onOpenChange={setHistoryModalOpen}>
          <DialogContent className="sm:max-w-lg">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <History className="w-5 h-5" />
                Historial de modificaciones
              </DialogTitle>
            </DialogHeader>
            <div className="max-h-[60vh] overflow-y-auto">
              {loadingHistory ? (
                <div className="flex items-center justify-center py-8">
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-sky-600"></div>
                </div>
              ) : historyData.length > 0 ? (
                <div className="space-y-4 py-4">
                  {historyData.map((entry, idx) => (
                    <div key={idx} className="border-l-2 border-sky-200 pl-4 pb-4">
                      <div className="flex items-center gap-2 text-sm text-slate-500 mb-2">
                        <User className="w-4 h-4" />
                        <span className="font-medium text-slate-700">{entry.edited_by_name}</span>
                        <span>-</span>
                        <span>
                          {format(new Date(entry.edited_at), "d MMM yyyy, HH:mm", { locale: es })}
                        </span>
                      </div>
                      <div className="space-y-1">
                        {Object.entries(entry.changes || {}).map(([field, change]) => (
                          <div key={field} className="text-sm bg-slate-50 p-2 rounded">
                            <span className="font-medium">{formatFieldName(field)}:</span>
                            <span className="text-slate-500 ml-2">modificado</span>
                          </div>
                        ))}
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-center py-8 text-slate-500">
                  No hay historial de modificaciones
                </div>
              )}
            </div>
          </DialogContent>
        </Dialog>
      </div>
    </OwnerLayout>
  );
};

export default Reports;
